import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Shield, Volume2, AlertTriangle, PlayCircle, Info } from 'lucide-react';
import { useLabStatus, useLabPrograms, useUpdateLab } from '../../hooks/useLabAPI';
import PageHeader from '../../components/PageHeader';
import ConsentModal from './ConsentModal';
import { quantizeDb, formatDb } from '@/utils/tinnitusDisplay';

/**
 * Lab Settings Page - Safe Notch Configuration
 * 
 * BUILD 52.5: Uses centralized tinnitusDisplay utilities for consistent dB display
 * 
 * DESIGN PREMIUM & APAISANT:
 * - Gain affiché avec quantification 0.5 dB
 * - Couleurs désaturées et apaisantes
 * - Bouton "Suspendre" discret
 * - Messages de sécurité rassurants
 * - Tooltip explicatif sur badge "Recommandé"
 */

// Safety constants
const INVERSE_NOTCH_GAIN_DEFAULT = 3;
const INVERSE_NOTCH_GAIN_MAX = 6;
const CLASSIC_NOTCH_GAIN_FIXED = -6;

const getLang = () => localStorage.getItem('hearen_lang') || 'fr';

// BUILD 52.5: Format gain using quantized values (0.5 dB steps) without aggressive "+" sign
const formatGain = (value) => {
  const quantized = quantizeDb(value);
  const numStr = Number.isInteger(quantized) 
    ? quantized.toString() 
    : quantized.toFixed(1);
  return `${numStr} dB`;
};

// Tooltip content for "Recommended" badge
const recommendedTooltip = {
  fr: {
    title: 'Pourquoi recommandé ?',
    text: "L'Inverse Notch applique une stimulation douce et progressive autour de votre fréquence d'acouphène.\n\nCette approche vise à favoriser l'adaptation auditive tout en respectant des limites de sécurité strictes.\n\nElle est proposée par défaut car elle est généralement mieux tolérée par les utilisateurs, tout en restant optionnelle."
  },
  en: {
    title: 'Why recommended?',
    text: "Inverse Notch applies gentle, progressive stimulation around your tinnitus frequency.\n\nThis approach aims to promote auditory adaptation while respecting strict safety limits.\n\nIt is offered by default as it is generally better tolerated by users, while remaining optional."
  }
};

const translations = {
  fr: {
    safetyMessage: 'La stimulation est limitée pour votre confort et votre sécurité.',
    inverseTitle: 'Inverse Notch',
    recommended: 'Recommandé',
    beta: 'Bêta',
    gentleStimulation: 'Stimulation douce et progressive à la fréquence cible.',
    classicTitle: 'Classic Notch',
    conservativeMode: 'Atténuation ciblée (mode conservateur)',
    frequency: 'Fréquence',
    band: 'Bande',
    halfOctave: '½ octave',
    gain: 'Correction',
    active: 'Actif',
    activate: 'Activer',
    suspend: 'Suspendre', // Changed from "Désactiver"
    switchToInverse: '→ Mode recommandé',
    valuesFromTest: 'Valeurs calibrées depuis votre dernier test',
    noTestWarning: 'Aucun test d\'acouphène',
    noTestInfo: 'Effectuez d\'abord un test pour calibrer les programmes.',
    goToTest: 'Faire un test',
    viewPlan: 'Voir le plan complet',
    saved: 'Sauvegardé',
    error: 'Erreur',
    loading: 'Chargement...',
    consentRequired: 'Consentement requis',
    consentText: 'Veuillez accepter les conditions d\'utilisation du Lab Mode pour continuer.',
    acceptConsent: 'Accepter et continuer',
    labUnavailable: 'Lab Mode n\'est pas disponible actuellement.',
    modalTitle: 'Mode avancé — Classic Notch',
    modalBody: 'Ce mode atténue une zone précise du spectre sonore.\nLe rendu audio peut être sensiblement modifié et ne convient pas à tous.',
    modalReassurance: 'Vous pouvez revenir au mode recommandé à tout moment.',
    modalActivate: 'Activer le Classic Notch',
    modalCancel: 'Annuler'
  },
  en: {
    safetyMessage: 'Stimulation is safely limited for your comfort.',
    inverseTitle: 'Inverse Notch',
    recommended: 'Recommended',
    beta: 'Beta',
    gentleStimulation: 'Gentle and progressive stimulation at the target frequency.',
    classicTitle: 'Classic Notch',
    conservativeMode: 'Targeted attenuation (conservative mode)',
    frequency: 'Frequency',
    band: 'Band',
    halfOctave: '½ octave',
    gain: 'Correction',
    active: 'Active',
    activate: 'Activate',
    suspend: 'Suspend', // Changed from "Deactivate"
    switchToInverse: '→ Recommended mode',
    valuesFromTest: 'Values calibrated from your last test',
    noTestWarning: 'No tinnitus test',
    noTestInfo: 'Complete a test first to calibrate the programs.',
    goToTest: 'Take a test',
    viewPlan: 'View full plan',
    saved: 'Saved',
    error: 'Error',
    loading: 'Loading...',
    consentRequired: 'Consent required',
    consentText: 'Please accept the Lab Mode terms of use to continue.',
    acceptConsent: 'Accept and continue',
    labUnavailable: 'Lab Mode is not available at this time.',
    modalTitle: 'Advanced mode — Classic Notch',
    modalBody: 'This mode attenuates a specific area of the sound spectrum.\nThe audio rendering may be noticeably altered and may not suit everyone.',
    modalReassurance: 'You can return to the recommended mode at any time.',
    modalActivate: 'Enable Classic Notch',
    modalCancel: 'Cancel'
  }
};

// Classic Notch Consent Modal - Calming design
const ClassicNotchConsentModal = ({ onAccept, onCancel }) => {
  const t = translations[getLang()] || translations.fr;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-500 to-slate-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-white/90" />
            <h3 className="text-lg font-semibold text-white">{t.modalTitle}</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{t.modalBody}</div>
          <Alert className="border-blue-100 bg-blue-50/50">
            <Shield className="h-4 w-4 text-blue-500" />
            <AlertDescription className="text-blue-700/80 text-sm">{t.modalReassurance}</AlertDescription>
          </Alert>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onCancel} className="flex-1 text-gray-600" data-testid="classic-notch-cancel-btn">
              {t.modalCancel}
            </Button>
            <Button onClick={onAccept} className="flex-1 bg-slate-500 hover:bg-slate-600" data-testid="classic-notch-confirm-btn">
              {t.modalActivate}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LabSettings = ({ t: propT }) => {
  const navigate = useNavigate();
  const { status, loading: statusLoading, error: statusError, refetch: refetchStatus } = useLabStatus();
  const { programs, loading: programsLoading, refetch: refetchPrograms } = useLabPrograms();
  const { updateLab, updating } = useUpdateLab();

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showClassicNotchConsent, setShowClassicNotchConsent] = useState(false);
  const [showRecommendedTooltip, setShowRecommendedTooltip] = useState(false);
  const [toast, setToast] = useState(null);
  const recommendedTooltipRef = useRef(null);

  const t = translations[getLang()] || translations.fr;
  const tooltipContent = recommendedTooltip[getLang()] || recommendedTooltip.fr;

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recommendedTooltipRef.current && !recommendedTooltipRef.current.contains(event.target)) {
        setShowRecommendedTooltip(false);
      }
    };
    if (showRecommendedTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showRecommendedTooltip]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleConsentAccepted = async () => {
    try {
      await updateLab({ consent: { accepted: true, version: "1.0" } });
      showToast(t.saved);
      refetchStatus();
      refetchPrograms();
      setShowConsentModal(false);
    } catch (error) {
      showToast(t.error, 'error');
    }
  };

  const handleActivateInverse = async () => {
    if (!status?.consent_accepted) {
      setShowConsentModal(true);
      return;
    }
    try {
      await updateLab({
        programs: {
          inverse_notch: {
            enabled: true,
            f_t: programs?.inverse_notch?.f_t || programs?.notch?.f_t,
            band: '1/2_oct',
            gain_db: Math.min(programs?.inverse_notch?.gain_db || INVERSE_NOTCH_GAIN_DEFAULT, INVERSE_NOTCH_GAIN_MAX),
            hard_cap_dbfs: -1.0
          }
        },
        active_program: 'inverse_notch'
      });
      showToast(t.saved);
      refetchPrograms();
    } catch (error) {
      showToast(t.error, 'error');
    }
  };

  const handleRequestClassicNotch = () => {
    if (!status?.consent_accepted) {
      setShowConsentModal(true);
      return;
    }
    setShowClassicNotchConsent(true);
  };

  const handleActivateNotch = async () => {
    try {
      await updateLab({
        programs: {
          notch: {
            enabled: true,
            f_t: programs?.notch?.f_t,
            band: '1/2_oct',
            gain_db: CLASSIC_NOTCH_GAIN_FIXED,
            q_factor: 1.2
          }
        },
        active_program: 'notch'
      });
      showToast(t.saved);
      setShowClassicNotchConsent(false);
      refetchPrograms();
    } catch (error) {
      showToast(t.error, 'error');
    }
  };

  const handleSuspend = async () => {
    try {
      await updateLab({ active_program: null });
      showToast(t.saved);
      refetchPrograms();
    } catch (error) {
      showToast(t.error, 'error');
    }
  };

  if (statusLoading || programsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-8 pb-40">
        <div className="max-w-4xl mx-auto">
          <PageHeader t={propT} />
          <div className="text-center py-12">{t.loading}</div>
        </div>
      </div>
    );
  }

  if (statusError === 'LAB_DISABLED' || (status && !status.lab_enabled)) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-8 pb-40">
        <div className="max-w-4xl mx-auto">
          <PageHeader t={propT} />
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <AlertDescription className="text-red-700">{t.labUnavailable}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const activeProgram = programs?.active_program;
  const tinnitusFrequency = programs?.notch?.f_t || programs?.inverse_notch?.f_t;
  const inverseGain = programs?.inverse_notch?.gain_db || INVERSE_NOTCH_GAIN_DEFAULT;
  const hasValidTest = tinnitusFrequency && tinnitusFrequency > 0;

  const isInverseActive = activeProgram === 'inverse_notch';
  const isClassicActive = activeProgram === 'notch';

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-8 pb-40">
      <div className="max-w-4xl mx-auto">
        <PageHeader t={propT} />

        {/* Toast */}
        {toast && (
          <Alert className={`mb-4 ${toast.type === 'error' ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <AlertDescription className={toast.type === 'error' ? 'text-red-700' : 'text-emerald-700'}>
              {toast.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Consent Banner */}
        {!status?.consent_accepted && (
          <Card className="mb-6 border-2 border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-2">{t.consentRequired}</h3>
                  <p className="text-amber-700/80 text-sm mb-4">{t.consentText}</p>
                  <Button onClick={() => setShowConsentModal(true)} className="bg-amber-500 hover:bg-amber-600">
                    {t.acceptConsent}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safety Message - Calming */}
        <Alert className="mb-6 border-blue-100 bg-gradient-to-r from-blue-50/50 to-slate-50/50">
          <Shield className="h-5 w-5 text-blue-500" />
          <AlertDescription className="text-blue-700/80">{t.safetyMessage}</AlertDescription>
        </Alert>

        {/* No Test Warning */}
        {!hasValidTest && (
          <Alert className="mb-6 border-amber-200 bg-amber-50/50">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <AlertDescription className="text-amber-700">
              <strong>{t.noTestWarning}</strong><br/>
              {t.noTestInfo}
              <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/test')}>
                {t.goToTest}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* ==================== INVERSE NOTCH (RECOMMENDED) ==================== */}
        <Card className={`mb-6 ${isInverseActive ? 'ring-2 ring-amber-400/60 shadow-lg' : 'border-2 border-amber-100'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xl font-semibold">{t.inverseTitle}</span>
              {/* Badges - hierarchical */}
              {/* Recommended badge with info tooltip */}
              <div className="relative inline-flex items-center" ref={recommendedTooltipRef}>
                <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                  {t.recommended}
                </span>
                <button
                  onClick={() => setShowRecommendedTooltip(!showRecommendedTooltip)}
                  className="ml-1 p-0.5 text-blue-500 hover:text-blue-600 focus:outline-none"
                  aria-label="Info"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                {/* Tooltip */}
                {showRecommendedTooltip && (
                  <div className="absolute left-0 top-full mt-2 w-80 p-4 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                    <p className="text-base font-bold text-blue-700 mb-3">{tooltipContent.title}</p>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{tooltipContent.text}</p>
                  </div>
                )}
              </div>
              <span className="px-2.5 py-1 bg-amber-500/80 text-white text-xs font-semibold rounded-full">
                {t.beta}
              </span>
              {isInverseActive && (
                <span className="px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                  {t.active}
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-amber-700/80 font-medium mt-1">
              {t.gentleStimulation}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasValidTest ? (
              <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-br from-amber-500/90 to-amber-600/90 rounded-xl shadow">
                <div>
                  <p className="text-sm text-amber-100/80">{t.frequency}</p>
                  <p className="text-lg font-bold text-white">{tinnitusFrequency?.toLocaleString()} Hz</p>
                </div>
                <div>
                  <p className="text-sm text-amber-100/80">{t.band}</p>
                  <p className="text-lg font-semibold text-white">{t.halfOctave}</p>
                </div>
                <div>
                  <p className="text-sm text-amber-100/80">{t.gain}</p>
                  {/* Desaturated gain without aggressive "+" */}
                  <p className="text-lg font-semibold text-amber-100">{formatGain(inverseGain)}</p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-100 rounded-lg text-center text-gray-500">
                {t.noTestInfo}
              </div>
            )}

            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Volume2 size={14} />
              {t.valuesFromTest}
            </p>

            {/* Active: discrete "Suspend", Inactive: prominent "Activate" */}
            {isInverseActive ? (
              <Button
                onClick={handleSuspend}
                disabled={updating}
                variant="ghost"
                className="w-full h-10 text-sm text-amber-600/70 hover:text-amber-700 hover:bg-amber-50 border border-amber-200/50"
                data-testid="suspend-inverse-notch-btn"
              >
                {t.suspend}
              </Button>
            ) : (
              <Button
                onClick={handleActivateInverse}
                disabled={!status?.consent_accepted || !hasValidTest || updating}
                className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600 shadow"
                data-testid="activate-inverse-notch-btn"
              >
                <PlayCircle size={18} className="mr-2" />
                {t.activate}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* ==================== CLASSIC NOTCH (CONSERVATIVE) ==================== */}
        <Card className={`mb-6 ${isClassicActive ? 'ring-2 ring-slate-400/60 shadow-lg' : 'border border-gray-200 opacity-85'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xl font-semibold">{t.classicTitle}</span>
              {isClassicActive && (
                <span className="px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                  {t.active}
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-slate-500 font-medium mt-1">
              {t.conservativeMode}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasValidTest ? (
              <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-br from-slate-500/90 to-slate-600/90 rounded-xl shadow">
                <div>
                  <p className="text-sm text-slate-200/80">{t.frequency}</p>
                  <p className="text-lg font-bold text-white">{tinnitusFrequency?.toLocaleString()} Hz</p>
                </div>
                <div>
                  <p className="text-sm text-slate-200/80">{t.band}</p>
                  <p className="text-lg font-semibold text-white">{t.halfOctave}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-200/80">{t.gain}</p>
                  <p className="text-lg font-semibold text-slate-200">{formatGain(CLASSIC_NOTCH_GAIN_FIXED)}</p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-100 rounded-lg text-center text-gray-500">
                {t.noTestInfo}
              </div>
            )}

            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Volume2 size={14} />
              {t.valuesFromTest}
            </p>

            {isClassicActive ? (
              <div className="space-y-2">
                <Button
                  onClick={handleActivateInverse}
                  disabled={updating}
                  className="w-full h-11 text-sm bg-amber-500 hover:bg-amber-600"
                >
                  {t.switchToInverse}
                </Button>
                <Button
                  onClick={handleSuspend}
                  disabled={updating}
                  variant="ghost"
                  className="w-full h-9 text-xs text-slate-500/70 hover:text-slate-600 hover:bg-slate-50 border border-slate-200/50"
                  data-testid="suspend-notch-btn"
                >
                  {t.suspend}
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleRequestClassicNotch}
                disabled={!status?.consent_accepted || !hasValidTest || updating}
                variant="outline"
                className="w-full h-11 text-sm border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="activate-notch-btn"
              >
                <PlayCircle size={16} className="mr-2" />
                {t.activate}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Back to Plan */}
        <Button variant="outline" onClick={() => navigate('/progress/plan')} className="w-full text-gray-600">
          {t.viewPlan} →
        </Button>

        {/* Consent Modal */}
        {showConsentModal && (
          <ConsentModal t={propT} onAccept={handleConsentAccepted} onCancel={() => setShowConsentModal(false)} />
        )}

        {/* Classic Notch Consent Modal */}
        {showClassicNotchConsent && (
          <ClassicNotchConsentModal onAccept={handleActivateNotch} onCancel={() => setShowClassicNotchConsent(false)} />
        )}
      </div>
    </div>
  );
};

export default LabSettings;
